/**
 * resolver.ts — the pure entitlement resolution function.
 *
 * resolve(billingDetails) → { features, limits, fees }
 *
 * Rules (from decisions.md + entitlement-architecture.md):
 *   features(org) = TIER_FEATURES[plan] ∪ override_profile(org)
 *
 * ADDITIVE UNION ONLY — there is no subtract/deny operation. A feature is
 * withheld only by being absent from every granting set.
 *
 * HARD INVARIANT: this file must NEVER read billingDetails.processingFees.tier.
 * That field is the fee-deal axis, not the entitlement axis. A test asserts this.
 */

import { CATALOG } from './catalog.generated.js';
import {
  PLAN_TO_TIER,
  OVERRIDE_PROFILES,
  type OrgBillingDetails,
  type ResolvedEntitlements,
  type ResolvedFees,
  type StoredFeeAmounts,
  type Plan,
  type Tier,
  type LimitValue,
} from './types.js';

// ---------------------------------------------------------------------------
// Fail-policy: this is an access-control + money library, so it FAILS LOUD on
// data-integrity bugs (an unknown plan or override) rather than silently
// granting/stripping access or returning a non-numeric fee. Callers wrap in
// try/catch and decide how to surface (deny + alert). Both languages raise the
// same way so the FE and the backends behave identically. See the resolver test
// suite + the Python EntitlementError mirror.
// ---------------------------------------------------------------------------

export class EntitlementError extends Error {}

export class UnknownPlanError extends EntitlementError {
  constructor(public readonly plan: string) {
    super(
      `Unknown plan "${plan}". Expected one of: ${Object.keys(PLAN_TO_TIER).join(', ')}. ` +
        `This is a data-integrity error — billingDetails.plan must be a known plan value.`,
    );
    this.name = 'UnknownPlanError';
  }
}

export class UnknownOverrideError extends EntitlementError {
  constructor(public readonly override: string) {
    super(
      `Unknown entitlementOverrides "${override}". Expected one of: ${OVERRIDE_PROFILES.join(', ')}. ` +
        `A stale or mistyped override would silently strip a grandfathered org's features — failing loud instead.`,
    );
    this.name = 'UnknownOverrideError';
  }
}

/**
 * Thrown when an org's catalog fee is the `'custom'` sentinel (a catalog tier with
 * no list price — Enterprise) but the org has no negotiated deal recorded (no
 * fee-negotiated override profile carrying stored feeAmounts). The catalog has no
 * list price for this tier, so returning the literal 'custom' string onto a money
 * path would silently become NaN downstream. Fail loud instead — a negotiated tier
 * without a recorded deal is a data-integrity bug.
 *
 * Historical name (PE-1700): the retired `feeOverride` mechanism is gone; a
 * negotiated deal now lives on `entitlementOverrides=legacy_custom` +
 * `billingDetails.feeAmounts`. An ENTERPRISE org that needs a negotiated rate must
 * carry that profile. The class name is retained so existing callers/tests that
 * catch it keep working.
 */
export class MissingFeeOverrideError extends EntitlementError {
  constructor(public readonly tier: string) {
    super(
      `Tier "${tier}" has custom (negotiated) pricing but no negotiated deal is recorded. ` +
        `A negotiated org must carry entitlementOverrides="legacy_custom" with billingDetails.feeAmounts. ` +
        `Returning the 'custom' sentinel onto a money path would become NaN downstream.`,
    );
    this.name = 'MissingFeeOverrideError';
  }
}

/**
 * Thrown when an org's override profile is fee-negotiated (e.g. legacy_custom,
 * `feeMode: negotiated`) but no usable `billingDetails.feeAmounts` was passed in —
 * so the engine cannot echo a stored rate and there is no catalog rate to fall back
 * to (falling back to the catalog STARTER rate would mischarge the 16 negotiated
 * orgs at ~8% vs their negotiated ~2.9%). Fail loud rather than emit a wrong number.
 *
 * The most likely real-world cause is a CALLER that did not lift the org record's
 * TOP-LEVEL `feeAmounts` into the OrgBillingDetails input (PE-1700).
 */
export class MissingNegotiatedFeeError extends EntitlementError {
  constructor(public readonly profile: string) {
    super(
      `Override profile "${profile}" is fee-negotiated but billingDetails.feeAmounts is absent or empty. ` +
        `The negotiated rate lives on the org record's feeAmounts — the caller must pass it into ` +
        `OrgBillingDetails.feeAmounts. Deriving the catalog rate here would mischarge a negotiated org.`,
    );
    this.name = 'MissingNegotiatedFeeError';
  }
}

// The catalog sentinel for a tier whose fee is negotiated per-deal (no list price).
const CUSTOM_FEE_SENTINEL = 'custom';

// `plan` is SPARSE: it is written only when an org has a Stripe subscription, so
// most orgs have no plan at all. Per architecture §6, an ABSENT plan means implicit
// STARTER (then the legacy-safe default + any backfilled override apply on top).
// Critically, this is the rollout reality: PE-1620 stamps `entitlementOverrides`,
// NOT `plan`, so a correctly-backfilled legacy org (override=legacy_starter, no
// plan) MUST resolve — not crash — the moment any gate or the charge path runs.
const DEFAULT_PLAN: Plan = 'STARTER';

// Coalesce an absent plan (undefined/null/missing key) to the implicit STARTER
// default. A PRESENT-but-invalid value ('FREE', '', 'garbage') is NOT absent — it
// is a data-integrity bug and is left to tierForPlan to reject. Absent ≠ invalid.
function effectivePlan(plan: string | null | undefined): string {
  return plan == null ? DEFAULT_PLAN : plan;
}

// Accepts `string | null | undefined` on purpose: billing.plan is typed `Plan` for
// well-typed callers, but at runtime this data comes from DynamoDB / JSON and is
// usually absent (sparse) or occasionally a stale/garbage value cast through `any`.
// Absent → STARTER (§6); a present-but-unknown value throws (fail-policy).
function tierForPlan(plan: string | null | undefined): Tier {
  const resolved = effectivePlan(plan);
  const tier = (PLAN_TO_TIER as Record<string, Tier | undefined>)[resolved];
  if (tier === undefined) throw new UnknownPlanError(resolved);
  return tier;
}

// ---------------------------------------------------------------------------
// Override profiles — named, frozen feature bundles for existing orgs
//
// legacy_starter / legacy_custom: all built features EXCEPT marketing (frozen)
// grandfathered_base / plus_grandfathered: legacy ∪ Plus ∪ marketing
//
// "All built features except marketing" = every feature in allLiveFeatureKeys
// that is NOT in marketingFeatureKeys.
// ---------------------------------------------------------------------------

function buildLegacyBundle(): ReadonlySet<string> {
  const s = new Set<string>();
  for (const f of CATALOG.allLiveFeatureKeys) {
    if (!CATALOG.marketingFeatureKeys.has(f)) s.add(f);
  }
  return s;
}

// Features that are Plus-tier and/or marketing-group but are DELIBERATELY NOT
// granted to grandfathered orgs via the grandfather union (see buildGrandfatheredBundle).
// The set is EMPTY as of PE-1860 (2026-07-26): it previously held `broadcasts_sms` to keep
// grandfathered_base orgs SMS-off (SMS was Plus+ only, gated by sms_included_per_month=0 at
// BASE). PE-1860 flipped broadcasts_sms.base=included, so the BASE *tier column* now grants
// SMS to every base org — grandfathered included — and because resolveFeatures UNIONS the
// tier column with this bundle and never subtracts, the exclusion could no longer withhold
// SMS. It was removed rather than left as a misleading no-op. NOTE the removal is NOT a pure
// no-op for one edge state: a grandfathered override sitting on the STARTER *plan*
// ({plan: STARTER, override: grandfathered_base | plus_grandfathered}) now resolves
// broadcasts_sms=true via the UNION half (the grandfather bundle includes Plus features),
// whereas STARTER's own tier column is still false. This is harmless — STARTER has
// sms_included_per_month=0, so planIncludesSmsMetering is false and trackSmsUsage gates it out
// (no Stripe line item, no metering) — and this plan+override pairing is not a real population
// (grandfathered overrides travel with their matching BASE/PLUS plan). Pinned by the golden
// case `starter-grandfathered-sms-not-metered`. Kept as a live mechanism: add a feature key
// here to withhold it from the grandfather union (the union half only) at a future date.
// MUST mirror the Python resolver's _GRANDFATHER_EXCLUDED_FEATURES.
const GRANDFATHER_EXCLUDED_FEATURES: ReadonlySet<string> = new Set([]);

function buildGrandfatheredBundle(): ReadonlySet<string> {
  // legacy bundle ∪ Plus features ∪ marketing features, MINUS the deliberate
  // grandfather exclusions (see GRANDFATHER_EXCLUDED_FEATURES).
  const s = new Set<string>(buildLegacyBundle());
  // Add all features that are included at Plus
  for (const [featureKey, row] of Object.entries(CATALOG.features)) {
    if (row['plus'] && !GRANDFATHER_EXCLUDED_FEATURES.has(featureKey)) s.add(featureKey);
  }
  // Add LIVE marketing features. NOTE: before the SMS split this loop was a
  // deliberate no-op — every marketing feature was `roadmap` (none built), so
  // none were in allLiveFeatureKeys. It is written as the full union so that the
  // day a marketing feature flips roadmap→included and is regenerated,
  // grandfathered orgs gain it automatically. The exclusion set carves out the
  // ones we do NOT want granted this way (broadcasts_sms).
  for (const f of CATALOG.marketingFeatureKeys) {
    if (CATALOG.allLiveFeatureKeys.has(f) && !GRANDFATHER_EXCLUDED_FEATURES.has(f)) s.add(f);
  }
  return s;
}

// Computed once at module load (catalog is static)
const LEGACY_BUNDLE: ReadonlySet<string> = buildLegacyBundle();
const GRANDFATHERED_BUNDLE: ReadonlySet<string> = buildGrandfatheredBundle();

function getOverrideSet(profile: string): ReadonlySet<string> {
  switch (profile) {
    case 'legacy_starter':
    case 'legacy_custom':
      return LEGACY_BUNDLE;
    case 'grandfathered_base':
    case 'plus_grandfathered':
      return GRANDFATHERED_BUNDLE;
    default:
      throw new UnknownOverrideError(profile);
  }
}

// ---------------------------------------------------------------------------
// Legacy-safe default (rollout safety — architecture doc §12, PE-1620 runbook)
//
// During the migration, an existing org that has NOT yet been stamped with an
// entitlementOverrides profile must NOT resolve to stock-Starter — that would
// strip every Base/Plus feature it has today (the exact catastrophe the
// "never strip a feature" principle forbids). So while legacy-safe mode is ON,
// an absent override on a STARTER plan resolves to the legacy_starter bundle.
//
//   step 1 (deploy): legacy-safe ON  → absent override + STARTER → legacy bundle
//   step 2: backfill stamps every existing org with an explicit override
//   step 3 (flip):  setLegacySafeDefault(false) → absent override + STARTER → stock
//   step 4: create_org stamps no override; net-new orgs now resolve to stock
//
// Default is ON (true): the non-destructive state, safe the moment it ships.
// The flip is a SINGLE config call, picked up by every consumer on next deploy.
//
// It applies ONLY to STARTER with NO override. Any explicit override always
// wins (additive union is unchanged); a non-STARTER plan is never affected
// (only existing orgs predate the model, and they all sat on Starter/custom).
// ---------------------------------------------------------------------------

let LEGACY_SAFE_DEFAULT = true;

/**
 * Flip the rollout-safety default. Pass `false` only AFTER the PE-1620 backfill
 * has stamped every existing org with an explicit override (runbook step 3).
 * Affects only the absent-override + STARTER case; everything else is unchanged.
 */
export function setLegacySafeDefault(enabled: boolean): void {
  LEGACY_SAFE_DEFAULT = enabled;
}

/** Current value of the legacy-safe default (for tests / introspection). */
export function getLegacySafeDefault(): boolean {
  return LEGACY_SAFE_DEFAULT;
}

/**
 * True when the legacy-safe default should be applied for this billing state:
 * legacy-safe mode is ON, the org carries NO explicit override, and it's on the
 * STARTER plan. `opts.legacySafeDefault` overrides the module default per-call
 * (used by tests and any consumer that wants to be explicit rather than rely on
 * global config).
 */
function appliesLegacySafeDefault(
  billing: OrgBillingDetails,
  opts?: ResolveOptions,
): boolean {
  const enabled = opts?.legacySafeDefault ?? LEGACY_SAFE_DEFAULT;
  // Absent plan ⇒ implicit STARTER (§6), so an unstamped org (no plan, no override)
  // gets the legacy bundle — which is the entire point of the legacy-safe default.
  return enabled && !billing.entitlementOverrides && effectivePlan(billing.plan) === 'STARTER';
}

// ---------------------------------------------------------------------------
// Tier feature set — all features included at `tier`
// ---------------------------------------------------------------------------

function getTierFeatures(tier: Tier): ReadonlySet<string> {
  const s = new Set<string>();
  for (const [featureKey, row] of Object.entries(CATALOG.features)) {
    if (row[tier]) s.add(featureKey);
  }
  return s;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Per-call options. `legacySafeDefault` overrides the module-level default for
 * this call only (omit to use the configured default — see setLegacySafeDefault).
 */
export interface ResolveOptions {
  legacySafeDefault?: boolean;
}

/**
 * Resolve an org's full entitlements from its billing state.
 * No I/O — all data comes from the in-process catalog + the passed billingDetails.
 */
export function resolve(billing: OrgBillingDetails, opts?: ResolveOptions): ResolvedEntitlements {
  const tier = tierForPlan(billing.plan);
  const features = resolveFeatures(billing, tier, opts);

  // Limits — return the tier's limit value for each key
  const limits: Record<string, LimitValue> = {};
  for (const [limitKey, row] of Object.entries(CATALOG.limits)) {
    limits[limitKey] = row[tier];
  }

  // Fees — the engine owns the complete number (never reads processingFees.tier).
  // NOTE: this can throw MissingFeeOverrideError (a catalog custom tier with no
  // negotiated deal) or MissingNegotiatedFeeError (a fee-negotiated profile with no
  // feeAmounts passed in). resolve() intentionally surfaces those — it returns fees.
  // The entitlement-only paths (is_entitled, getLimit) do NOT compute fees, so a
  // missing fee deal never blocks a feature/limit check (axis separation).
  const fees = resolveFees(billing);

  return { features, limits, fees };
}

/**
 * Compute the org's entitled feature set.
 * features(org) = TIER_FEATURES[plan] ∪ override_profile(org).
 * Pure entitlement axis — never touches fees.
 *
 * When no explicit override is present and the legacy-safe default applies
 * (legacy-safe mode ON + STARTER plan), the legacy_starter bundle is unioned in
 * — so an un-backfilled existing org keeps its features rather than being
 * stripped down to stock-Starter. This is still an additive union: it only ever
 * ADDS the legacy bundle, never removes a feature.
 */
function resolveFeatures(
  billing: OrgBillingDetails,
  tier: Tier,
  opts?: ResolveOptions,
): ReadonlySet<string> {
  const features = new Set<string>(getTierFeatures(tier));
  if (billing.entitlementOverrides) {
    for (const f of getOverrideSet(billing.entitlementOverrides)) {
      features.add(f);
    }
  } else if (appliesLegacySafeDefault(billing, opts)) {
    for (const f of LEGACY_BUNDLE) {
      features.add(f);
    }
  }
  return features;
}

/**
 * Boolean entitlement check.
 *
 * Computes features directly — deliberately NOT via resolve() — so an entitlement
 * check never depends on fee-data integrity. An ENTERPRISE org missing its
 * feeOverride can still have its features checked; only the fee paths throw.
 */
export function is_entitled(
  billing: OrgBillingDetails,
  feature: string,
  opts?: ResolveOptions,
): boolean {
  const tier = tierForPlan(billing.plan);
  return resolveFeatures(billing, tier, opts).has(feature);
}

/**
 * camelCase alias for {@link is_entitled} — the idiomatic TS name, alongside
 * `getLimit` / `resolveFees`. `is_entitled` is retained (and exported) because it
 * matches the Python API exactly, which keeps the cross-language / golden-fixture
 * story symmetric; new TS callers should prefer `isEntitled`.
 */
export const isEntitled = is_entitled;

/**
 * Usage limit for a key at the org's plan tier.
 * Returns 'unlimited', a number, or null (feature not available on this tier).
 */
export function getLimit(billing: OrgBillingDetails, limitKey: string): LimitValue {
  const tier = tierForPlan(billing.plan);
  const row = CATALOG.limits[limitKey];
  if (!row) return null;
  return row[tier];
}

/**
 * Is this org's override profile fee-negotiated?
 *
 * CATALOG-DRIVEN (PE-1700): reads `CATALOG.overrideFeeModes` (emitted from the
 * YAML `override_profiles` block), NOT a hardcoded string. Today only
 * `legacy_custom` carries `feeMode: negotiated`. A profile that is absent from the
 * map (or has no fee mode) is NOT negotiated → derives from the catalog.
 */
function isNegotiated(profile: string | null | undefined): boolean {
  if (!profile) return false;
  return CATALOG.overrideFeeModes[profile] === 'negotiated';
}

// Convert a stored dollar amount (string like "0.30", or a number) into INTEGER
// CENTS — matching the catalog fixed-fee representation. Absent/empty → 0 (a
// negotiated org with no fixed component charges $0 fixed, which is the common
// case: the 16 prod orgs carry platformFixedFee "0"). A non-numeric value is a
// data-integrity bug on the money path → throw.
function storedDollarsToCents(val: string | number | undefined | null, field: string): number {
  if (val === undefined || val === null || val === '') return 0;
  // Reject booleans explicitly — matches the Python peer's `isinstance(val, bool)`
  // guard (bool is an int subclass there). Without this, `Number(true)` is 1 →
  // Math.round(100) = 100 cents = a silent $1.00 fixed fee on the money path for a
  // runtime path that bypasses the type (JSON deserialization, an `as` cast, a
  // dynamic caller). Fail loud instead.
  if (typeof val === 'boolean') {
    throw new EntitlementError(
      `Stored feeAmounts.${field} must be a non-negative number, got a boolean ${JSON.stringify(val)}.`,
    );
  }
  const n = typeof val === 'number' ? val : Number(val);
  if (!Number.isFinite(n) || n < 0) {
    throw new EntitlementError(
      `Stored feeAmounts.${field} must be a non-negative number, got ${JSON.stringify(val)}.`,
    );
  }
  return Math.round(n * 100);
}

// Normalize a stored percent (string like "0.029", or a number) into a string
// echoed VERBATIM where it is already a string, or stringified when numeric.
// Absent/empty → "0" (a negotiated org may carry all the rate in one component
// and 0 in the other — the 16 prod orgs have platformPercentFee "0"). A
// non-numeric value is a money-path data bug → throw.
function storedPercentString(val: string | number | undefined | null, field: string): string {
  if (val === undefined || val === null || val === '') return '0';
  // Reject booleans explicitly — symmetric with storedDollarsToCents above and
  // with Python's _stored_percent_string `isinstance(val, bool)` guard. Without
  // it, Number(true)=1 would echo "1" (a 100% rate) with no error on a runtime
  // path that bypasses the type. Fail loud instead.
  if (typeof val === 'boolean') {
    throw new EntitlementError(
      `Stored feeAmounts.${field} must be a non-negative number, got a boolean ${JSON.stringify(val)}.`,
    );
  }
  const n = typeof val === 'number' ? val : Number(val);
  if (!Number.isFinite(n) || n < 0) {
    throw new EntitlementError(
      `Stored feeAmounts.${field} must be a non-negative number, got ${JSON.stringify(val)}.`,
    );
  }
  // Echo verbatim when already a string (never reformat a negotiated deal — an
  // over-eager toFixed could drift a stored "0.029" into "0.0290" or worse); when
  // numeric, stringify without imposing a decimal count.
  return typeof val === 'string' ? val : String(n);
}

// A feeAmounts object is "usable" if it carries at least one real (non-empty)
// component. An absent object, or one with every component empty/absent, cannot
// stand in for a negotiated rate — the caller almost certainly failed to lift the
// org's top-level feeAmounts into the input (PE-1700).
function hasUsableFeeAmounts(fa: StoredFeeAmounts | null | undefined): fa is StoredFeeAmounts {
  if (!fa) return false;
  return (
    [fa.platformPercentFee, fa.processingPercentFee, fa.platformFixedFee, fa.processingFixedFee].some(
      (v) => v !== undefined && v !== null && v !== '',
    )
  );
}

/**
 * Echo the org's stored negotiated rate (percent AND fixed) — PURE field access on
 * the passed input, no I/O, no catalog. Fixed dollars → integer cents (matching the
 * catalog representation). Percents echoed verbatim.
 */
function fromFeeAmounts(fa: StoredFeeAmounts): ResolvedFees {
  return {
    platformPct: storedPercentString(fa.platformPercentFee, 'platformPercentFee'),
    processingPct: storedPercentString(fa.processingPercentFee, 'processingPercentFee'),
    platformFixed: storedDollarsToCents(fa.platformFixedFee, 'platformFixedFee'),
    processingFixed: storedDollarsToCents(fa.processingFixedFee, 'processingFixedFee'),
  };
}

/**
 * Resolve the org's COMPLETE fee number — the engine owns it end to end (PE-1700).
 *
 * Two paths, selected by the CATALOG-driven fee mode of the org's override profile:
 *   • Negotiated (entitlementOverrides profile has feeMode: negotiated — today
 *     legacy_custom): echo billingDetails.feeAmounts (percent AND fixed) VERBATIM.
 *     Pure field access on the passed input. Throws if feeAmounts is missing/empty
 *     (deriving the catalog rate would mischarge — the caller must lift the org's
 *     top-level feeAmounts in).
 *   • Any catalog tier (STARTER/BASE/PLUS/PRO/…): ONE uniform derive path from the
 *     catalog using plan + coveredCommitment, now returning fixed fees too. No
 *     STARTER special-case; adding a tier is a catalog YAML row (open/closed).
 *
 * The retired `feeOverride` short-circuit is GONE (PE-1700 — feeOverride was
 * percent-only, read by nobody, abandoned-before-adoption).
 *
 * HARD INVARIANT: never reads billingDetails.processingFees.tier.
 * The processingFees.tier field is the negotiated FEE DEAL — it is NOT an
 * entitlement input and must never influence is_entitled or fee derivation.
 */
export function resolveFees(billing: OrgBillingDetails): ResolvedFees {
  // Validate the plan FIRST — a garbage plan is a data-integrity bug regardless of
  // any fee deal, so the fail-policy (unknown plan → throw) holds on every path and
  // keeps resolveFees consistent with resolve(). The tier is discarded on the
  // negotiated path; the validation is the point.
  const tier = tierForPlan(billing.plan);

  // Negotiated path — echo the stored rate. The profile's fee mode is catalog data.
  if (isNegotiated(billing.entitlementOverrides)) {
    if (!hasUsableFeeAmounts(billing.feeAmounts)) {
      throw new MissingNegotiatedFeeError(billing.entitlementOverrides as string);
    }
    return fromFeeAmounts(billing.feeAmounts);
  }

  // Uniform catalog-derive path — every standard tier, incl. fixed fees.
  const covered = billing.coveredCommitment ?? false;

  const platformPct = covered
    ? CATALOG.fees.platformFeeCovered[tier]
    : CATALOG.fees.platformFeeUncovered[tier];

  const processingPct = CATALOG.fees.processingFee[tier];

  // A 'custom' sentinel means the tier has negotiated pricing with no list price
  // (Enterprise). Reaching here means the org is NOT on a fee-negotiated override
  // profile (that path returned above), so there is no recorded deal — fail loud
  // rather than emit a non-numeric money value. Guards the fixed components too.
  if (platformPct === CUSTOM_FEE_SENTINEL || processingPct === CUSTOM_FEE_SENTINEL) {
    throw new MissingFeeOverrideError(tier);
  }

  // Fixed fees are integer cents straight from the catalog (0 for all standard
  // tiers today). null only occurs for enterprise/custom, already rejected above.
  const platformFixed = CATALOG.fees.platformFixedFee[tier];
  const processingFixed = CATALOG.fees.processingFixedFee[tier];

  return { platformPct, processingPct, platformFixed, processingFixed };
}

/**
 * Is this org on the `legacy_custom` override profile?
 *
 * `legacy_custom` orgs are negotiated, manually-provisioned merchants (custom
 * fee deal, no self-serve subscription). Several UI surfaces treat them as a
 * MANAGED ACCOUNT — they must not see the self-serve billing screen or a
 * self-serve upgrade CTA, and are instead routed to a human ("contact us").
 *
 * This is a PRESENTATION property of the override PROFILE, deliberately NOT
 * derivable from the resolved feature set: `legacy_custom` and
 * `grandfathered_base` share nearly the same features, but only `legacy_custom`
 * is manually managed. So the profile name is the correct, single discriminator
 * — centralized here so the magic string lives in exactly one place (consumed
 * by the FE EntitlementsContext + the Settings tab gating).
 *
 * Pure string check on the profile field — never reads fees, never throws.
 */
export function isLegacyCustom(billing: OrgBillingDetails | null | undefined): boolean {
  return billing?.entitlementOverrides === 'legacy_custom';
}

// Re-export catalog for consumers that need raw access (e.g. tests, FE provider)
export { CATALOG };
